'use client';

import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
// import { Card } from '@/app/components/card';
import { Bell, Search, Loader2, ChevronDown, ChevronUp, Languages } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { loadCharacterChatHistoryAction, loadPaginatedPublicAvatarsActionOptimized } from '../../lib/actions';
import { useTags } from '@/app/lib/avatar-tags/useTags';
import type { Character } from '@/app/lib/types/character';
import NotificationPopup from '@/app/recent-chat/mobile/NotificationPopup';
import { useNavbar } from '@/app/lib/contexts/NavbarContext';
import DiscordLink from '@/app/components/discordLink';
import '@/app/ui/scrollbar.css';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { getPresignedUrl } from '@/app/lib/actions/s3';
import { AdvancedImage } from '@cloudinary/react';
import { createCloudinaryImages, createCloudinaryImageSync, isCloudinaryReady, preloadCloudinary, createCloudinaryImagesWithPreload } from '@/app/lib/cloudinary/cloudinary-image-utils';
import type { CloudinaryPreloadResult } from '@/app/lib/cloudinary/cloudinary-preload';
import LanguageSelectPopup from '@/app/user-and-session/languages/ui/LanguageSelectPopup';
import { useLocale } from '@/app/user-and-session/languages/i18n';
import type { Locale } from '@/app/user-and-session/languages/i18n/config';
import SUPPORTED_LANGUAGES from '@/app/user-and-session/languages/languages';

interface DesktopDiscoverCharactersProps {
  initialAvatars: {
    success: boolean;
    avatars: Character[] | null;
    message: string;
    hasMore: boolean;
  };
  cloudinaryPreload: CloudinaryPreloadResult | null;
}

function LoadingState() {
  const t = useTranslations('discover');
  return (
    <div className="flex flex-col items-center gap-6 p-6">
      <Loader2 className="w-12 h-12 animate-spin text-white" />
      <p className="text-white">{t('loadingCharacters')}</p>
    </div>
  );
}

// === Layout Configurable Parameters ===
const MIN_CARD_WIDTH = 180; // px
const MIN_GAP = 4;          // px
const MAX_GAP = 6;          // px

export default function DesktopDiscoverCharacters({ initialAvatars, cloudinaryPreload }: DesktopDiscoverCharactersProps) {
  const router = useRouter();
  const t = useTranslations('discover');
  const tTags = useTranslations('tags');
  const { isCollapsed: navbarCollapsed } = useNavbar();
  const { userEmail } = useAuth();
  const [userAppDataState] = useUserAppData(userEmail || '');
  const { locale } = useLocale();

  // Data state  
  const [avatars, setAvatars] = useState<Character[]>(() => {
    // Remove duplicates from initial data
    const initialData = initialAvatars.avatars || [];
    const seen = new Set();
    return initialData.filter(avatar => {
      const duplicate = seen.has(avatar.metadata.avatar_id);
      seen.add(avatar.metadata.avatar_id);
      return !duplicate;
    });
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState<boolean>(initialAvatars.hasMore || true);
  const [offset, setOffset] = useState(initialAvatars.avatars?.length || 0);
  
  // Cloudinary images state - maps avatar_id to cloudinary image object
  const [cloudinaryImages, setCloudinaryImages] = useState<Record<string, any>>({});

  // UI state
  const [showSearchWindow, setShowSearchWindow] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Notification popup state
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  
  // Language popup state
  const [showLanguagePopup, setShowLanguagePopup] = useState(false);
  const languageName = SUPPORTED_LANGUAGES[locale] || SUPPORTED_LANGUAGES['en'];

  // Tab and filter state
  const [activeMainTab, setActiveMainTab] = useState('trending');
  const [genderFilter, setGenderFilter] = useState<'all' | 'male' | 'female' | 'non-binary'>('all');
  const [includeFiltered, setIncludeFiltered] = useState<boolean>(false);
  const includeFilteredRef = useRef<boolean>(false);
  
  // Sync ref with state
  useEffect(() => {
    includeFilteredRef.current = includeFiltered;
  }, [includeFiltered]);

  // Tags state
  const { tags: allTagsList, isLoading: isLoadingTags } = useTags();
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [showAllTagRows, setShowAllTagRows] = useState(false);

  // Add SearchStatus type
  const SearchStatus = {
    Idle: 'Idle',
    Loading: 'Loading',
    Loaded: 'Loaded',
    NoResults: 'NoResults',
  } as const;
  type SearchStatusType = typeof SearchStatus[keyof typeof SearchStatus];

  // Add searchStatus state
  const [searchStatus, setSearchStatus] = useState<SearchStatusType>(SearchStatus.Idle);

  // Initialize Cloudinary images using preloaded data (MUCH FASTER!)
  useEffect(() => {
    const initializeCloudinaryImages = () => {
      // Only initialize if we have valid preload data
      if (!cloudinaryPreload?.cloudName || !cloudinaryPreload?.processedAvatars?.length) {
        console.warn('Cloudinary preload data is missing or invalid');
        return;
      }

      // Use preloaded data for instant initialization
      const cloudinaryImages = createCloudinaryImagesWithPreload(
        cloudinaryPreload.cloudName,
        cloudinaryPreload.processedAvatars
      );

      setCloudinaryImages(cloudinaryImages);
      console.log(`Initialized ${Object.keys(cloudinaryImages).length} Cloudinary images`);
    };

    initializeCloudinaryImages();
  }, [cloudinaryPreload]); // Re-run when cloudinaryPreload changes

  // Fast synchronous processing for new avatars (after Cloudinary is initialized)
  const processNewAvatarsSync = useCallback((newAvatars: Character[]) => {
    if (!isCloudinaryReady() || newAvatars.length === 0) return;

    const newCloudinaryImages: Record<string, any> = {};
    
    newAvatars.forEach(avatar => {
      const publicId = avatar.metadata.coverImgPresignedUrl || avatar.metadata.presignedUrl;
      if (publicId) {
        const cloudinaryImg = createCloudinaryImageSync(publicId);
        if (cloudinaryImg) {
          newCloudinaryImages[avatar.metadata.avatar_id] = cloudinaryImg;
        }
      }
    });

    if (Object.keys(newCloudinaryImages).length > 0) {
      setCloudinaryImages(prev => ({
        ...prev,
        ...newCloudinaryImages
      }));
    }
  }, []);

  // Store image keys and character metadata in session storage whenever avatars are loaded
  useEffect(() => {
    const cacheAvatarData = async () => {
      const userEmail = userAppDataState.userEmail;
      const userId = userAppDataState.userId;
      
      // Process all avatars in parallel using Promise.all with map
      await Promise.all(avatars.map(async (avatar) => {
        // Cache character metadata if not already cached
        if (avatar.metadata.avatar_id) {
          if (!sessionStorage.getItem(`chat_${avatar.metadata.avatar_id}_metadata`)) {
            try {
              const metadataJson = JSON.stringify(avatar.metadata);
              sessionStorage.setItem(`chat_${avatar.metadata.avatar_id}_metadata`, metadataJson);
            } catch (error) {
              console.error('Failed to cache character metadata:', error);
            }
          }
        }
        var chatHistoryResult = null;
        // Cache Chat History for authenticated users if not already cached
        if (false && !sessionStorage.getItem(`chat_${avatar.metadata.avatar_id}_${userId}_chatHistory`)) {
          try {
            chatHistoryResult = await loadCharacterChatHistoryAction(avatar.metadata.avatar_id, userEmail);
            if (chatHistoryResult.success && chatHistoryResult.chatHistory?.hasHistory) {
              sessionStorage.setItem(`chat_${avatar.metadata.avatar_id}_${userId}_chatHistory`, JSON.stringify(chatHistoryResult.chatHistory));
            }
            else if (chatHistoryResult.success && !chatHistoryResult.chatHistory?.hasHistory) {
              const emptyChatHistory = {
                hasHistory: false,
                messages: [],
              };
              sessionStorage.setItem(`chat_${avatar.metadata.avatar_id}_${userId}_chatHistory`, JSON.stringify(emptyChatHistory));
            }
          } catch (error) {
            console.error('Failed to cache chat history:', error);
          }
        }
        
        // Get last image, then get s3 image key, then get presigned URL
        // if (!sessionStorage.getItem(`image_${avatar.metadata.avatar_id}_imageKey`)) {
        //   if (chatHistoryResult) {
        //     var imageKey = null;
        //     const chatHistory = chatHistoryResult?.chatHistory;
        //     try {
        //       if (chatHistory?.messages && chatHistory.messages.length > 0) {
        //         // Find the last message with an image (search from the end)
        //         for (let i = chatHistory.messages.length - 1; i >= 0; i--) {
        //           const message = chatHistory.messages[i];
        //           if (message.imageUrl && message.imageUrl.trim() !== '') {
        //             imageKey = message.imageUrl;
        //             break;
        //           }
        //         }
        //       }
        //     } catch (error) {
        //       console.error('Failed to parse cached chat history:', error);
        //     }
        //   }
        //   // If no image found in chat history, use metadata image
        //   if (!imageKey && avatar.metadata.image_uri) {
        //     imageKey = avatar.metadata.image_uri;
        //   }
        //   // Get presigned URL and store in local storage
        //   if (imageKey) {
        //     try {
        //       const presignedUrl = (await getPresignedUrl(imageKey)).presignedUrl;
        //       // Store presigned URL in local storage
        //       sessionStorage.setItem(`image_${avatar.metadata.avatar_id}_imageKey`, imageKey);
        //     } catch (error) {
        //       console.error('Failed to get presigned URL for image:', error);
        //     }  
        //   }
        // }
      }));
    };
    
    cacheAvatarData();
  }, [avatars]);



  // Observer for infinite scrolling
  const observer = useRef<IntersectionObserver | null>(null);
  const lastAvatarElementRef = useCallback((node: HTMLDivElement | null) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreAvatars();
      }
    });
    
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  // Unified reload function that handles both locale changes and filter toggles
  const reloadInitialAvatars = useCallback(async (options?: { localeOverride?: Locale; includeFilteredOverride?: boolean }) => {
    setIsLoading(true);
    try {
      const sortBy = activeMainTab === 'trending' ? 'score' : 'time';
      const currentLocale = options?.localeOverride ?? locale;
      const currentIncludeFiltered = options?.includeFilteredOverride ?? includeFilteredRef.current;
      
      // Update ref if override provided
      if (options?.includeFilteredOverride !== undefined) {
        includeFilteredRef.current = options.includeFilteredOverride;
      }
      
      const result = await loadPaginatedPublicAvatarsActionOptimized(0, 30, searchTerm, sortBy, genderFilter, selectedTags, currentIncludeFiltered, currentLocale);
      
      if (result.success && result.avatars) {
        const processedAvatars = result.avatars.map(avatar => ({
          ...avatar,
          metadata: {
            ...avatar.metadata,
            create_time: new Date(avatar.metadata.create_time)
          }
        }));
        
        setAvatars(processedAvatars);
        setOffset(processedAvatars.length);
        setHasMore(result.hasMore || false);
        if (searchTerm.trim()) {
          setSearchStatus(processedAvatars.length === 0 ? SearchStatus.NoResults : SearchStatus.Loaded);
        } else {
          setSearchStatus(SearchStatus.Idle);
        }
        // Process avatar images for Cloudinary (fast sync)
        processNewAvatarsSync(processedAvatars);
      } else {
        setAvatars([]);
        setHasMore(false);
        setSearchStatus(SearchStatus.NoResults);
      }
    } catch (error) {
      console.error('Error reloading avatars:', error);
      setAvatars([]);
      setHasMore(false);
      setSearchStatus(SearchStatus.NoResults);
    } finally {
      setIsLoading(false);
    }
  }, [activeMainTab, searchTerm, genderFilter, selectedTags, locale]);

  // Reload avatars when locale changes
  useEffect(() => {
    reloadInitialAvatars({ localeOverride: locale });
  }, [locale, reloadInitialAvatars]);

  // Load more avatars
  const loadMoreAvatars = async () => {
    if (isLoading || !hasMore) return;
    
    setIsLoading(true);
    try {
      const sortBy = activeMainTab === 'trending' ? 'score' : 'time';
      const result = await loadPaginatedPublicAvatarsActionOptimized(offset, 30, '', sortBy, genderFilter, selectedTags, includeFilteredRef.current, locale);
      
      if (result.success && result.avatars && result.avatars.length > 0) {
        const processedAvatars = result.avatars.map(avatar => ({
          ...avatar,
          metadata: {
            ...avatar.metadata,
            create_time: new Date(avatar.metadata.create_time)
          }
        }));
        
        setAvatars(prev => {
          // Create a map of existing avatar IDs to avoid duplicates
          const existingIds = new Set(prev.map(avatar => avatar.metadata.avatar_id));
          const newAvatars = processedAvatars.filter(avatar => !existingIds.has(avatar.metadata.avatar_id));
          // Process new avatar images for Cloudinary (fast sync)
          processNewAvatarsSync(newAvatars);
          return [...prev, ...newAvatars];
        });
        setOffset(prev => prev + processedAvatars.length);
        setHasMore(result.hasMore || false);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more avatars:', error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };



  // Search functionality
  const handleSearch = () => {
    // Always trigger a search/reload when Enter is pressed
    setSearchStatus(SearchStatus.Loading);
    handleTabChange(activeMainTab, searchTerm.trim());
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    // If user clears the search input completely, reset to original state
    if (!newValue.trim() && searchStatus !== SearchStatus.Idle) {
      setSearchStatus(SearchStatus.Idle);
    }
  };

  // Tab change handler
  const handleTabChange = async (tab: string, search?: string) => {
    setActiveMainTab(tab);
    setIsLoading(true);
    setSearchStatus(SearchStatus.Loading);
    
    try {
      const sortBy = tab === 'trending' ? 'score' : 'time';
      const result = await loadPaginatedPublicAvatarsActionOptimized(0, 30, search || '', sortBy, genderFilter, selectedTags, includeFilteredRef.current, locale);
      
      if (result.success && result.avatars) {
        const processedAvatars = result.avatars.map(avatar => ({
          ...avatar,
          metadata: {
            ...avatar.metadata,
            create_time: new Date(avatar.metadata.create_time)
          }
        }));
        
        setAvatars(processedAvatars);
        setOffset(processedAvatars.length);
        setHasMore(result.hasMore || false);
        setSearchStatus(processedAvatars.length === 0 ? SearchStatus.NoResults : SearchStatus.Loaded);
        // Process avatar images for Cloudinary (fast sync)
        processNewAvatarsSync(processedAvatars);
      } else {
        setAvatars([]);
        setHasMore(false);
        setSearchStatus(SearchStatus.NoResults);
      }
    } catch (error) {
      console.error('Error changing tab:', error);
      setAvatars([]);
      setHasMore(false);
      setSearchStatus(SearchStatus.NoResults);
    } finally {
      setIsLoading(false);
    }
  };

  // Gender filter handler
  const handleGenderFilterChange = async (newGender: typeof genderFilter, overrideIncludeFiltered?: boolean) => {
    setGenderFilter(newGender);
    setIsLoading(true);
    
    try {
      const sortBy = activeMainTab === 'trending' ? 'score' : 'time';
      const currentIncludeFiltered = overrideIncludeFiltered ?? includeFiltered;
      const result = await loadPaginatedPublicAvatarsActionOptimized(0, 30, searchTerm, sortBy, newGender, selectedTags, currentIncludeFiltered);
      
      if (result.success && result.avatars) {
        const processedAvatars = result.avatars.map(avatar => ({
          ...avatar,
          metadata: {
            ...avatar.metadata,
            create_time: new Date(avatar.metadata.create_time)
          }
        }));
        
        setAvatars(processedAvatars);
        setOffset(processedAvatars.length);
        setHasMore(result.hasMore || false);
        // Process avatar images for Cloudinary (fast sync)
        processNewAvatarsSync(processedAvatars);
      }
    } catch (error) {
      console.error('Error filtering by gender:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Tag filter handler
  const handleTagFilterChange = async (tagId: number) => {
    const newSelectedTags = selectedTags.includes(tagId)
      ? selectedTags.filter(id => id !== tagId)
      : [...selectedTags, tagId];
    
    setSelectedTags(newSelectedTags);
    setIsLoading(true);
    
    try {
      const sortBy = activeMainTab === 'trending' ? 'score' : 'time';
      const result = await loadPaginatedPublicAvatarsActionOptimized(0, 30, searchTerm, sortBy, genderFilter, newSelectedTags, includeFilteredRef.current);
      
      if (result.success && result.avatars) {
        const processedAvatars = result.avatars.map(avatar => ({
          ...avatar,
          metadata: {
            ...avatar.metadata,
            create_time: new Date(avatar.metadata.create_time)
          }
        }));
        
        setAvatars(processedAvatars);
        setOffset(processedAvatars.length);
        setHasMore(result.hasMore || false);
        // Process avatar images for Cloudinary (fast sync)
        processNewAvatarsSync(processedAvatars);
      }
    } catch (error) {
      console.error('Error filtering by tags:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle notification bell click
  const handleNotificationClick = () => {
    setShowNotificationPopup(true);
  };

  const gridRef = useRef<HTMLDivElement>(null);

  if (!initialAvatars.success || !initialAvatars.avatars) {
    return (
      <div className="flex flex-col items-center gap-6 p-6">
        <h2 className="text-xl font-semibold mb-4 text-white">{t('exploreAvatars')}</h2>
        <div className="p-4 bg-red-100 text-red-700 rounded-lg">
          {initialAvatars.message || t('failedToLoadAvatars')}
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<LoadingState />}>
      <div className="bg-[#121214] w-full flex flex-col h-screen">
        {/* Header Section with Tags */}
        <header className="relative bg-[#121214] pt-4 pb-4 px-6 flex flex-col gap-2 z-10 flex-shrink-0">
          {/* First Row - Search Bar and Notification */}
          <div className="flex items-center justify-between">
            {/* Centered Search Bar */}
            <div className="flex-1 flex justify-center">
              <div className={`flex h-10 flex-shrink-0 cursor-pointer justify-end rounded-full bg-[#2a2a2e] transition-all duration-300 ${showSearchWindow ? 'max-w-[800px] w-full border border-[#ffffff40]' : 'w-[48px] hover:border-hover-border'}`}>
                <div className="pointer-events-auto flex h-full items-center justify-center text-2xl w-12">
                  <Search 
                    className={`w-5 h-5 ${showSearchWindow ? 'text-[rgba(255,255,255,0.7)]' : 'text-white'}`}
                    onClick={() => setShowSearchWindow(true)}
                  />
                </div>
                {showSearchWindow && (
                  <div className="pointer-events-auto flex-1">
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchTerm}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                      placeholder={t('searchCharacters')}
                      className="search-input h-full w-full pl-1.5 text-sm text-white bg-transparent outline-none placeholder:text-[rgba(255,255,255,0.7)]"
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Discord Link Button */}
            <DiscordLink>
              <button
                className="w-11 h-11 bg-[#ffffff1a] rounded-3xl text-white hover:bg-[#ffffff33] transition-colors duration-200 flex items-center justify-center mx-2"
                aria-label="Join Discord"
              >
                <svg 
                  className="w-8 h-8" 
                  viewBox="0 0 24 24" 
                  fill="#5865F2"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.019 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z"/>
                </svg>
              </button>
            </DiscordLink>

            {/* Language Setting Button */}
            <button
              className="w-11 h-11 bg-[#ffffff1a] rounded-3xl text-white hover:bg-[#ffffff33] transition-colors duration-200 flex items-center justify-center mr-2"
              aria-label="Language Settings"
              onClick={() => setShowLanguagePopup(true)}
            >
              <Languages className="w-6 h-6 text-gray-300" />
            </button>
          </div>

          {/* Second Row - Tags Toggle and Filters */}
          <div className="flex items-center justify-between">
            {/* Tags Toggle Button */}
            <button
              onClick={() => setShowAllTagRows(!showAllTagRows)}
              className="px-4 py-2 rounded-xl bg-[#222327] text-white text-sm font-medium border border-[#d2d5da40] hover:bg-[#2a2a2e] transition-all duration-200 flex items-center gap-2"
            >
              <span className="font-['Montserrat',Helvetica]">
                {showAllTagRows ? t('hideAllTags') : t('viewAllTags')}
              </span>
              {isLoadingTags ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : showAllTagRows ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>

            {/* Filters */}
            <div className="flex items-center gap-3">
              {/* Filtered Content Toggle */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-300">{t('filtered')}</span>
                <button
                  onClick={() => {
                    const newValue = !includeFiltered;
                    setIncludeFiltered(newValue);
                    includeFilteredRef.current = newValue;
                    // Use unified reload function
                    reloadInitialAvatars({ includeFilteredOverride: newValue });
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                    includeFiltered ? 'bg-indigo-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                      includeFiltered ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {/* Sort Dropdown */}
              <div className="relative">
                <select
                  value={activeMainTab}
                  onChange={(e) => handleTabChange(e.target.value)}
                  className="px-4 py-2 rounded-xl bg-[#222327] text-white text-sm font-medium border border-[#d2d5da40] outline-none cursor-pointer font-['Montserrat',Helvetica] hover:bg-[#2a2a2e] focus:bg-[#2a2a2e] transition-all duration-200 appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 16px center',
                    backgroundSize: '16px',
                    paddingRight: '48px'
                  }}
                >
                  <option value="trending" className="bg-[#1a1a1e] text-white">{t('sortByTrending')}</option>
                  <option value="latest" className="bg-[#1a1a1e] text-white">{t('sortByLatest')}</option>
                </select>
              </div>

              {/* Gender Filter Dropdown */}
              <div className="relative">
                <select
                  value={genderFilter}
                  onChange={(e) => handleGenderFilterChange(e.target.value as typeof genderFilter)}
                  className="px-4 py-2 rounded-xl bg-[#222327] text-white text-sm font-medium border border-[#d2d5da40] outline-none cursor-pointer font-['Montserrat',Helvetica] hover:bg-[#2a2a2e] focus:bg-[#2a2a2e] transition-all duration-200 appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 16px center',
                    backgroundSize: '16px',
                    paddingRight: '48px'
                  }}
                >
                  <option value="all" className="bg-[#1a1a1e] text-white">{t('allGenders')}</option>
                  <option value="male" className="bg-[#1a1a1e] text-white">{t('male')}</option>
                  <option value="female" className="bg-[#1a1a1e] text-white">{t('female')}</option>
                  <option value="non-binary" className="bg-[#1a1a1e] text-white">{t('nonBinary')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Third Row - All Tags Display */}
          {showAllTagRows && (
            <div className="flex flex-wrap gap-2">
              {allTagsList.map(tag => (
                <button
                  key={tag.tag_id}
                  onClick={() => handleTagFilterChange(tag.tag_id)}
                  className={`px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 border ${
                    selectedTags.includes(tag.tag_id)
                      ? "bg-[#ffffff1a] text-white border-[#ffffff40] shadow-[0px_0px_5px_#ffffff20]"
                      : "text-gray-400 hover:text-white hover:bg-[#ffffff0a] border-[#ffffff20] hover:border-[#ffffff40]"
                  }`}
                >
                  <span className="font-['Montserrat',Helvetica]">{tTags(tag.tag_name)}</span>
                </button>
              ))}
              {allTagsList.length === 0 && !isLoadingTags && (
                <div className="text-gray-400 text-sm">{t('noTagsAvailable')}</div>
              )}
            </div>
          )}
        </header>

        {/* Content Section - Grid layout */}
        <div className="flex-1 px-8 pb-6 overflow-y-auto dark-scrollbar">
          <div
            ref={gridRef}
            className="grid"
            style={{
              gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_CARD_WIDTH}px, 1fr))`,
              gap: `clamp(${MIN_GAP}px, 1vw, ${MAX_GAP}px)`,
              width: '100%',
              maxWidth: '100%'
            }}
          >
            {avatars.map((avatar, index) => {
              const isLastElement = index === avatars.length - 1;
              return (
                <Link
                  key={`${avatar.metadata.avatar_id}-${index}`}
                  href={`/chat/${avatar.metadata.avatar_id}`}
                >
                  <Card
                    ref={isLastElement ? lastAvatarElementRef : undefined}
                    className="discover-card relative rounded-[13.79px] overflow-hidden border-0 transition-transform duration-300 ease-in-out cursor-pointer hover:scale-[1.02]"
                    style={{
                      width: '100%',
                      aspectRatio: '9 / 16',
                      maxWidth: '100%',
                      flexShrink: 0
                    }}
                  >
                    {avatar.metadata.presignedUrl ? (
                      <>
                        {cloudinaryImages[avatar.metadata.avatar_id] ? (
                          <AdvancedImage
                            cldImg={cloudinaryImages[avatar.metadata.avatar_id]}
                            alt={avatar.metadata.avatar_name}
                            className="object-cover w-full h-full pointer-events-none"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-700 rounded-[13.79px] animate-pulse" />
                        )}
                        {/* Character info overlay - positioned at the bottom */}
                        <div className="absolute bottom-0 w-full">
                          {/* Fade to black gradient overlay */}
                          <div className="absolute bottom-0 w-full h-20 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none"></div>
                          {/* Character text with proper padding */}
                          <div className="relative p-3 z-20">
                            <h3 className="w-full font-['Montserrat',Helvetica] font-semibold text-white text-[13.8px] leading-normal truncate mb-1">
                              {avatar.metadata.avatar_name}
                            </h3>
                            <p className="self-stretch font-['Montserrat',Helvetica] font-normal text-neutral-300 text-xs leading-snug overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                              {avatar.metadata.agent_bio || avatar.metadata.prompt}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-gray-700 rounded-[13.79px] animate-pulse" />
                    )}
                  </Card>
                </Link>
              );
            })}
          </div>
          
          {/* Status messages outside of grid */}
          {/* Loading indicator */}
          {isLoading && (
            <div className="w-full flex justify-center py-4 mt-6">
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-white" />
                <span className="text-white">{t('loadingMoreCharacters')}</span>
              </div>
            </div>
          )}
          {searchStatus === SearchStatus.NoResults && (
            <div className="w-full text-center py-4 text-gray-400 mt-6">
              {t('noMatchingResult')}
            </div>
          )}
          {/* End of results message */}
          {!hasMore && !isLoading && avatars.length > 0 && (
            <div className="w-full text-center py-4 text-gray-400 mt-6">
              {t('foundCharacters', { count: avatars.length })}
            </div>
          )}
        </div>
        {/* Notification Popup */}
        <NotificationPopup 
          isOpen={showNotificationPopup}
          onClose={() => setShowNotificationPopup(false)}
        />
        
        {/* Language Selection Popup */}
        <LanguageSelectPopup
          isOpen={showLanguagePopup}
          onClose={() => setShowLanguagePopup(false)}
        />
      </div>
    </Suspense>
  );
}